import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table, Typography, Input, Button, Alert, Tooltip, Select, DatePicker,
  Popover, Badge, Space, Tag,
} from 'antd';
import { ReloadOutlined, SearchOutlined, EyeOutlined, FilterOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { api } from '../../services/api';
import { useClient } from '../../context/ClientContext';
import { EstadoPagoChip } from '../../shared/components/EstadoPagoChip';
import { EstadoPedidoChip } from '../../shared/components/EstadoPedidoChip';
import { SkStyleTag, skeletonColumns, skeletonRows } from '../../shared/components/TableSkeleton';
import { hexAlpha } from '../../utils/colorUtils';

const { Text } = Typography;

const fmt = (iso) =>
  iso ? new Date(iso).toLocaleString('es-AR', { dateStyle: 'short', timeStyle: 'short' }) : '—';

const CircleBtn = ({ icon, onClick, title, danger }) => (
  <Tooltip title={title}>
    <button type="button" onClick={onClick} style={{
      width: 32, height: 32, border: `1px solid ${danger ? '#fca5a5' : '#e5e7eb'}`,
      borderRadius: '50%', background: '#fff', display: 'inline-flex',
      alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
      color: danger ? '#ef4444' : '#374151', fontSize: 14, flexShrink: 0,
      transition: 'border-color 0.15s',
    }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = danger ? '#ef4444' : '#374151'; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = danger ? '#fca5a5' : '#e5e7eb'; }}
    >{icon}</button>
  </Tooltip>
);

export const PedidosAdmin = () => {
  const { clientId, theme , clientPath} = useClient();
  const primary = theme?.primaryColor || '#0f172a';
  const navigate = useNavigate();
  const [pedidos, setPedidos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [q, setQ]             = useState('');
  const [estado, setEstado]   = useState(undefined);
  const [desde, setDesde]     = useState(null);
  const [hasta, setHasta]     = useState(null);
  const [estados, setEstados] = useState([]);
  const [filtrosOpen, setFiltrosOpen] = useState(false);

  const filtrosActivos = [estado, desde, hasta, q].filter(Boolean).length;

  const cargar = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.admin.getPedidos();
      if (res.status === 'success') setPedidos(res.data || []);
      else setError(res.message || 'Error al cargar pedidos');
    } catch {
      setError('Error al conectar con el servidor');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    api.admin.getPedidoEstados().then(res => { if (res.status === 'success') setEstados(res.data || []); });
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const filtrados = pedidos.filter(p => {
    if (q) {
      const ql = q.toLowerCase();
      const matchQ = String(p.id).includes(ql) || p.nombre_cliente?.toLowerCase().includes(ql) || p.email_cliente?.toLowerCase().includes(ql);
      if (!matchQ) return false;
    }
    if (estado && p.estado_codigo !== estado) return false;
    if (desde && dayjs(p.fecha_alta).isBefore(desde, 'day')) return false;
    if (hasta && dayjs(p.fecha_alta).isAfter(hasta, 'day')) return false;
    return true;
  });

  const estadoOptions = estados.map(e => ({ label: e.nombre, value: e.codigo }));

  const columns = [
    {
      title: 'Cliente', key: 'cliente', minWidth: 200,
      render: (_, p) => {
        const nombre = p.nombre_cliente || '—';
        const initials = nombre.split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase();
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 40, height: 40, borderRadius: 8, flexShrink: 0,
              background: hexAlpha(primary, 0.10),
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: primary, fontWeight: 700, fontSize: 13,
            }}>{initials}</div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: 14, color: primary, display: 'flex', alignItems: 'center', gap: 6 }}>
                {nombre}
                {p.origen === 'admin' && <Tag style={{ marginInlineEnd: 0 }}>Vendedor</Tag>}
              </div>
              {p.email_cliente && <div style={{ fontSize: 12, color: '#9ca3af', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.email_cliente}</div>}
            </div>
          </div>
        );
      },
    },
    {
      title: '#', dataIndex: 'id', key: 'id', width: 70,
      render: (id) => <Text type="secondary" style={{ fontSize: 13 }}>#{id}</Text>,
    },
    {
      title: 'Fecha', dataIndex: 'fecha_alta', key: 'fecha', width: 140,
      render: (v) => <Text style={{ fontSize: 13 }}>{fmt(v)}</Text>,
    },
    {
      title: 'Total', dataIndex: 'total', key: 'total', width: 120,
      render: (v) => <Text strong style={{ fontSize: 14 }}>$ {Number(v).toFixed(2)}</Text>,
    },
    {
      title: 'Estado pedido', key: 'estado', width: 140,
      render: (_, p) => <EstadoPedidoChip nombre={p.estado_nombre} descripcion={p.estado_desc} color={p.estado_color} />,
    },
    {
      title: 'Estado pago', key: 'pago', width: 140,
      render: (_, p) => <EstadoPagoChip nombre={p.estado_pago_nombre} descripcion={p.estado_pago_desc} color={p.estado_pago_color} />,
    },
    {
      title: 'Método', dataIndex: 'metodo_pago', key: 'metodo', width: 130,
      render: (v) => <Text type="secondary" style={{ fontSize: 13 }}>{v || '—'}</Text>,
    },
    {
      title: '', key: 'acciones', width: 60, align: 'center',
      render: (_, p) => (
        <CircleBtn icon={<EyeOutlined />} title="Ver pedido" onClick={() => navigate(clientPath(`/admin/pedidos/${p.id}`))} />
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <Text strong style={{ fontSize: 28, lineHeight: 1.2 }}>Pedidos</Text>
        <Space>
          <Popover
            open={filtrosOpen}
            onOpenChange={setFiltrosOpen}
            trigger="click"
            placement="bottomRight"
            content={
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, width: 280 }}>
                <Input
                  prefix={<SearchOutlined style={{ color: '#9ca3af' }} />}
                  placeholder="Buscar por #, cliente o email..."
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  allowClear
                />
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  <DatePicker
                    value={desde}
                    onChange={(v) => setDesde(v)}
                    placeholder="Desde"
                    format="DD/MM/YYYY"
                    allowClear
                    style={{ flex: 1 }}
                  />
                  <span style={{ color: '#999' }}>—</span>
                  <DatePicker
                    value={hasta}
                    onChange={(v) => setHasta(v)}
                    placeholder="Hasta"
                    format="DD/MM/YYYY"
                    allowClear
                    style={{ flex: 1 }}
                  />
                </div>
                <Select
                  value={estado}
                  onChange={(v) => setEstado(v)}
                  options={estadoOptions}
                  placeholder="Filtrar por estado"
                  style={{ width: '100%' }}
                  allowClear
                />
              </div>
            }
          >
            <Badge count={filtrosActivos} size="small">
              <Button icon={<FilterOutlined />}>Filtros</Button>
            </Badge>
          </Popover>
          <Tooltip title="Actualizar">
            <Button type="text" icon={<ReloadOutlined />} onClick={cargar} loading={loading} />
          </Tooltip>
        </Space>
      </div>

      {filtrosActivos > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
          {desde && (
            <Tag closable onClose={() => setDesde(null)}>
              Desde: {desde.format('DD/MM/YYYY')}
            </Tag>
          )}
          {hasta && (
            <Tag closable onClose={() => setHasta(null)}>
              Hasta: {hasta.format('DD/MM/YYYY')}
            </Tag>
          )}
          {estado && (
            <Tag closable onClose={() => setEstado(undefined)}>
              Estado: {estadoOptions.find(o => o.value === estado)?.label || estado}
            </Tag>
          )}
          {q && (
            <Tag closable onClose={() => setQ('')}>
              Búsqueda: {q}
            </Tag>
          )}
        </div>
      )}

      {!loading && !error && (
        <Text type="secondary" style={{ fontSize: 13, marginBottom: 12, display: 'block' }}>
          {filtrados.length} pedido{filtrados.length !== 1 ? 's' : ''}
        </Text>
      )}

      {error && <Alert type="error" message={error} style={{ marginBottom: 16 }} />}

      <SkStyleTag />
      <Table
        columns={loading ? skeletonColumns(columns, { avatarIndex: 0, twoLineIndexes: [0] }) : columns}
        dataSource={loading ? skeletonRows(9) : filtrados}
        rowKey={loading ? 'key' : 'id'}
        size="middle"
        scroll={{ x: 'max-content' }}
        onRow={loading ? undefined : (p) => ({ onClick: () => navigate(clientPath(`/admin/pedidos/${p.id}`)), style: { cursor: 'pointer' } })}
        locale={{ emptyText: 'No hay pedidos.' }}
        pagination={loading ? false : { pageSize: 20, showSizeChanger: false, style: { padding: '8px 16px' } }}
        style={{ background: '#fff', borderRadius: 8 }}
      />
    </div>
  );
};
